import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import MapaUbicacion from 'shared/components/MapaUbicacion';
import API from 'shared/api';
import { FiX, FiMapPin, FiExternalLink } from 'react-icons/fi';

const DRAFT_KEY = 'agorax_draft_sede';
const EMPTY_SEDE = { nombre: '', lat: 8.6, lng: -70.2, direccion: '', zona_cobertura: '' };

function Sedes({ readOnly = false }) {
  const { isSupervisor, isAdmin, tieneVisibilidad } = useAuth();
  const v = (c) => tieneVisibilidad(c);
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const [sedes, setSedes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [eliminando, setEliminando] = useState(null);
  const [togglendo, setTogglendo] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [maxSedes, setMaxSedes] = useState(null);
  const [form, setForm] = useState(EMPTY_SEDE);

  // Reabrir el formulario con el borrador pendiente tras una recarga
  const draftRehidratadoRef = useRef(false);
  useEffect(() => {
    if (draftRehidratadoRef.current) return;
    draftRehidratadoRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = { ...EMPTY_SEDE, ...JSON.parse(raw) };
      const tieneContenido = Object.keys(draft).some(
        (k) => k !== 'lat' && k !== 'lng' && draft[k] && String(draft[k]).trim() !== ''
      );
      if (tieneContenido) {
        setForm(draft);
        setEditando(null);
        setMostrarForm(true);
      }
    } catch (e) {
      /* noop */
    }
  }, []);

  // Persistir borrador SOLO mientras se crea una sede nueva (no en edicion)
  useEffect(() => {
    if (mostrarForm && !editando) {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      } catch (e) {
        /* noop */
      }
    }
  }, [form, mostrarForm, editando]);

  const cargarSedes = async () => {
    try {
      if (isSupervisor()) {
        const res = await API.get('/sedes/mi-sede');
        if (res.data.status === 'success' && res.data.data) setSedes([res.data.data]);
      } else {
        const res = await API.get('/sedes/listar');
        if (res.data.status === 'success') setSedes(res.data.data);
        try {
          const sub = await API.get('/suscripciones/mi-suscripcion');
          if (sub.data.status === 'success') setMaxSedes(sub.data.data?.plan_max_sedes ?? null);
        } catch (e) {
          /* noop */
        }
      }
    } catch (e) {
      console.error('Error cargando sedes:', e);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarSedes();
  }, []); // eslint-disable-line

  const eliminarSede = (codigo) => setDeleteModal(codigo);

  const confirmarEliminar = async () => {
    const codigo = deleteModal;
    setDeleteModal(null);
    setEliminando(codigo);
    try {
      await API.delete(`/sedes/eliminar/${codigo}`);
      setSedes((prev) => prev.filter((s) => s.codigo !== codigo));
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al eliminar sede');
    } finally {
      setEliminando(null);
    }
  };

  const toggleSede = async (s) => {
    setTogglendo(s.codigo);
    try {
      const res = await API.put(`/sedes/${s.codigo}/toggle`);
      const activa = res.data?.data?.activa;
      setSedes((prev) => prev.map((x) => (x.codigo === s.codigo ? { ...x, activa } : x)));
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al cambiar estado');
    } finally {
      setTogglendo(null);
    }
  };

  const guardar = async () => {
    if (!form.nombre) return alert('El nombre de la sede es requerido');
    if (form.lat === null || form.lng === null) return alert('Selecciona la ubicación en el mapa');
    setGuardando(true);
    try {
      const params = new URLSearchParams();
      params.append('nombre', form.nombre);
      params.append('lat', form.lat);
      params.append('lng', form.lng);
      if (form.direccion) params.append('direccion', form.direccion);
      if (form.zona_cobertura) params.append('zona_cobertura', form.zona_cobertura);
      if (editando) {
        await API.put(`/sedes/modificar/${editando}?${params.toString()}`);
        setSedes((prev) =>
          prev.map((s) =>
            s.codigo === editando
              ? {
                  ...s,
                  nombre: form.nombre,
                  direccion: form.direccion,
                  zona_cobertura: form.zona_cobertura,
                  lat: form.lat,
                  lng: form.lng,
                }
              : s
          )
        );
        alert('Sede actualizada.');
      } else {
        const res = await API.post(`/sedes/crear?${params.toString()}`);
        const d = res.data?.data;
        if (d && d.activa === false) {
          alert(
            `Sede creada como INACTIVA. Tu plan permite ${d.limite_sedes} sede(s) activa(s). Mejora tu plan o desactiva otra.`
          );
        } else {
          alert('Sede creada.');
        }
      }
      setMostrarForm(false);
      setEditando(null);
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        /* noop */
      }
      cargarSedes();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar sede');
    } finally {
      setGuardando(false);
    }
  };

  const abrirFormNuevo = () => {
    let draft = EMPTY_SEDE;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) draft = { ...EMPTY_SEDE, ...JSON.parse(raw) };
    } catch (e) {
      /* noop */
    }
    setForm(draft);
    setEditando(null);
    setMostrarForm(true);
  };

  const editarSede = (sede) => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) {
      /* noop */
    }
    setForm({
      nombre: sede.nombre,
      lat: sede.lat || 8.6,
      lng: sede.lng || -70.2,
      direccion: sede.direccion || '',
      zona_cobertura: sede.zona_cobertura || '',
    });
    setEditando(sede.codigo);
    setMostrarForm(true);
  };

  if (cargando)
    return (
      <div style={S.wrapper}>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--agx-faint)' }}>
          Cargando sedes...
        </div>
      </div>
    );

  const activas = sedes.filter((s) => s.activa).length;
  const inactivas = sedes.filter((s) => !s.activa).length;

  return (
    <div style={S.wrapper}>
      <style>{`
        @media (max-width: 768px) {
          .sedes-wrapper { padding: 14px 10px !important; }
          .sedes-header { flex-direction: column !important; gap: 12px !important; align-items: flex-start !important; }
          .sedes-stats { grid-template-columns: repeat(2, 1fr) !important; gap: 6px !important; }
          .sedes-table-view { display: none !important; }
          .sedes-cards-view { display: block !important; }
          .sedes-modal { max-width: 95vw !important; padding: 18px !important; max-height: 95vh !important; overflow-y: auto !important; }
        }
        @media (min-width: 769px) {
          .sedes-table-view { display: block !important; }
          .sedes-cards-view { display: none !important; }
        }
      `}</style>

      <div className="sedes-header" style={S.header}>
        <div>
          <h2 style={S.title}>{isSupervisor() ? 'Mi Sede' : 'Gestion de Sedes'}</h2>
          {isAdmin() && !readOnly && (
            <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginTop: '2px' }}>
              Activas: <strong style={{ color: '#059669' }}>{activas}</strong> / {maxSedes ?? '∞'} ·
              Total: {sedes.length} sede(s)
            </div>
          )}
        </div>
        {isAdmin() && !readOnly && (
          <button onClick={abrirFormNuevo} style={S.btnAdd}>
            + Nueva Sede
          </button>
        )}
      </div>

      {/* Stats */}
      {isAdmin() && (
        <div className="sedes-stats" style={S.statsGrid}>
          <div style={{ ...S.statCard, borderTop: '3px solid #3b82f6' }}>
            <div style={{ ...S.statVal, color: '#3b82f6' }}>{sedes.length}</div>
            <div style={S.statLabel}>Total</div>
          </div>
          <div style={{ ...S.statCard, borderTop: '3px solid #10b981' }}>
            <div style={{ ...S.statVal, color: '#10b981' }}>{activas}</div>
            <div style={S.statLabel}>Activas</div>
          </div>
          <div style={{ ...S.statCard, borderTop: '3px solid #ef4444' }}>
            <div style={{ ...S.statVal, color: '#ef4444' }}>{inactivas}</div>
            <div style={S.statLabel}>Inactivas</div>
          </div>
          <div style={{ ...S.statCard, borderTop: '3px solid #f59e0b' }}>
            <div style={{ ...S.statVal, color: '#f59e0b' }}>{maxSedes ?? '∞'}</div>
            <div style={S.statLabel}>Limite Plan</div>
          </div>
        </div>
      )}

      {/* TABLE VIEW */}
      <div className="sedes-table-view" style={S.tableCard}>
        <table style={S.table}>
          <thead>
            <tr>
              {[
                'Código',
                'Nombre',
                v('vis.sedes.direccion') ? 'Dirección' : null,
                v('vis.sedes.zona') ? 'Cobertura' : null,
                'Mapa',
                'Estado',
                '',
              ]
                .filter(Boolean)
                .map((h) => (
                  <th key={h} style={S.th}>
                    {h}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {sedes.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    textAlign: 'center',
                    padding: '40px 14px',
                    color: 'var(--agx-faint)',
                    fontSize: '13px',
                  }}
                >
                  No hay sedes registradas
                </td>
              </tr>
            ) : (
              sedes.map((s) => (
                <tr key={s.codigo} style={S.tr}>
                  <td
                    style={{ ...S.td, fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}
                  >
                    {s.codigo}
                  </td>
                  <td style={{ ...S.td, fontWeight: 500 }}>{s.nombre}</td>
                  {v('vis.sedes.direccion') && (
                    <td
                      style={{
                        ...S.td,
                        fontSize: '12px',
                        maxWidth: '180px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.direccion || '-'}
                    </td>
                  )}
                  {v('vis.sedes.zona') && (
                    <td style={{ ...S.td, fontSize: '12px' }}>{s.zona_cobertura || '-'}</td>
                  )}
                  <td style={S.td}>
                    {s.lat && s.lng ? (
                      <a
                        href={`https://www.google.com/maps?q=${s.lat},${s.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          ...S.btnLink,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <FiExternalLink size={11} /> Ver
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td style={S.td}>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: s.activa ? '#ecfdf5' : '#fef2f2',
                        color: s.activa ? '#059669' : '#dc2626',
                      }}
                    >
                      {s.activa ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {!readOnly && (
                        <button onClick={() => editarSede(s)} style={S.btnEdit}>
                          Editar
                        </button>
                      )}
                      {isAdmin() && !readOnly && (
                        <button
                          onClick={() => toggleSede(s)}
                          disabled={togglendo === s.codigo}
                          style={{
                            ...S.btnEdit,
                            background: s.activa ? '#fffbeb' : '#ecfdf5',
                            color: s.activa ? '#d97706' : '#059669',
                          }}
                        >
                          {togglendo === s.codigo ? '...' : s.activa ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                      {isAdmin() && !readOnly && (
                        <button
                          onClick={() => eliminarSede(s.codigo)}
                          disabled={eliminando === s.codigo}
                          style={S.btnDel}
                        >
                          {eliminando === s.codigo ? '...' : 'Eliminar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* CARDS VIEW */}
      <div className="sedes-cards-view">
        {sedes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 14px', color: 'var(--agx-faint)' }}>
            No hay sedes registradas
          </div>
        ) : (
          sedes.map((s) => (
            <div key={s.codigo} style={S.mobileCard}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '10px',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--agx-text)' }}>
                    {s.nombre}
                  </div>
                  <div
                    style={{ fontSize: '12px', color: 'var(--agx-faint)', fontFamily: 'monospace' }}
                  >
                    {s.codigo}
                  </div>
                </div>
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: 600,
                    background: s.activa ? '#ecfdf5' : '#fef2f2',
                    color: s.activa ? '#059669' : '#dc2626',
                  }}
                >
                  {s.activa ? 'Activa' : 'Inactiva'}
                </span>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '6px',
                  marginBottom: '8px',
                }}
              >
                {v('vis.sedes.direccion') && (
                  <div>
                    <span style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>Dirección</span>
                    <div style={{ fontSize: '13px' }}>{s.direccion || '-'}</div>
                  </div>
                )}
                {v('vis.sedes.zona') && (
                  <div>
                    <span style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>Cobertura</span>
                    <div style={{ fontSize: '13px' }}>{s.zona_cobertura || '-'}</div>
                  </div>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  borderTop: '1px solid #f3f4f6',
                  paddingTop: '10px',
                  flexWrap: 'wrap',
                }}
              >
                {!readOnly && (
                  <button
                    onClick={() => editarSede(s)}
                    style={{ ...S.btnEdit, flex: 1, textAlign: 'center', minWidth: '60px' }}
                  >
                    Editar
                  </button>
                )}
                {isAdmin() && !readOnly && (
                  <button
                    onClick={() => toggleSede(s)}
                    disabled={togglendo === s.codigo}
                    style={{
                      ...S.btnEdit,
                      flex: 1,
                      textAlign: 'center',
                      minWidth: '80px',
                      background: s.activa ? '#fffbeb' : '#ecfdf5',
                      color: s.activa ? '#d97706' : '#059669',
                    }}
                  >
                    {togglendo === s.codigo ? '...' : s.activa ? 'Desactivar' : 'Activar'}
                  </button>
                )}
                {isAdmin() && !readOnly && (
                  <button
                    onClick={() => eliminarSede(s.codigo)}
                    disabled={eliminando === s.codigo}
                    style={{ ...S.btnDel, flex: 1, textAlign: 'center', minWidth: '60px' }}
                  >
                    {eliminando === s.codigo ? '...' : 'Eliminar'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* MODAL FORM */}
      {mostrarForm && (
        <div
          style={S.overlay}
          onClick={() => {
            setMostrarForm(false);
            setEditando(null);
          }}
        >
          <div className="sedes-modal" style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <h3
                style={{ fontSize: '18px', fontWeight: 700, color: 'var(--agx-text)', margin: 0 }}
              >
                {editando ? 'Editar Sede' : 'Nueva Sede'}
              </h3>
              <button
                onClick={() => {
                  setMostrarForm(false);
                  setEditando(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--agx-faint)',
                  fontSize: '20px',
                }}
              >
                <FiX />
              </button>
            </div>

            <div style={S.fg}>
              <label style={S.label}>Nombre de la sede *</label>
              <input
                style={S.input}
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Sede Centro"
                autoFocus
              />
            </div>

            <div style={S.fg}>
              <label style={S.label}>Dirección</label>
              <input
                style={S.input}
                value={form.direccion}
                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                placeholder="Calle 8, Barinas"
              />
            </div>

            <div style={S.fg}>
              <label style={S.label}>Zona de cobertura</label>
              <input
                style={S.input}
                value={form.zona_cobertura}
                onChange={(e) => setForm({ ...form, zona_cobertura: e.target.value })}
                placeholder="Zona centro"
              />
            </div>

            <div style={S.fg}>
              <label style={S.label}>
                Ubicación GPS *{' '}
                {form.lat && form.lng
                  ? `(${Number(form.lat).toFixed(4)}, ${Number(form.lng).toFixed(4)})`
                  : '— Selecciona en el mapa'}
              </label>
              <div
                style={{
                  height: isMobile ? '180px' : '220px',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1.5px solid #e2e4e9',
                }}
              >
                <MapaUbicacion
                  lat={form.lat || 8.6}
                  lng={form.lng || -70.2}
                  onLocationChange={(lat, lng) => setForm({ ...form, lat, lng })}
                />
              </div>
            </div>

            {!editando && (
              <div
                style={{
                  padding: '12px',
                  background: 'var(--agx-bg2)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--agx-muted)',
                  marginTop: '12px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <FiMapPin size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                Puedes configurar los métodos de pago de esta sede en{' '}
                <strong>Configuración &gt; Métodos de Pago</strong>.
              </div>
            )}

            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}
            >
              <button
                onClick={() => {
                  setMostrarForm(false);
                  setEditando(null);
                }}
                style={S.btnCancel}
              >
                Cancelar
              </button>
              <button onClick={guardar} disabled={guardando} style={S.btnSave}>
                {guardando ? 'Guardando...' : editando ? 'Guardar Cambios' : 'Crear Sede'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION */}
      {deleteModal && (
        <div style={S.overlay} onClick={() => setDeleteModal(null)}>
          <div
            style={{ ...S.modal, maxWidth: '420px', textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 10px', fontSize: '18px', color: 'var(--agx-text)' }}>
              Eliminar sede
            </h3>
            <p
              style={{
                margin: '0 0 6px',
                fontSize: '14px',
                color: 'var(--agx-muted)',
                lineHeight: 1.5,
              }}
            >
              Vas a eliminar la sede <strong>{deleteModal}</strong>.
            </p>
            <p style={{ margin: '0 0 18px', fontSize: '12px', color: '#dc2626', lineHeight: 1.5 }}>
              Se borrarán todos sus pedidos, inventario, métodos de pago, usuarios asignados y datos
              asociados. Esta acción no se puede deshacer.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button onClick={() => setDeleteModal(null)} style={S.btnCancel}>
                Cancelar
              </button>
              <button onClick={confirmarEliminar} style={{ ...S.btnSave, background: '#dc2626' }}>
                Si, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  wrapper: {
    padding: '24px 28px',
    background: 'var(--agx-bg2)',
    minHeight: '100vh',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    color: 'var(--agx-text)',
    letterSpacing: '-0.3px',
    margin: 0,
  },
  btnAdd: {
    padding: '10px 20px',
    borderRadius: '10px',
    border: 'none',
    background: '#111827',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: '10px',
    marginBottom: '20px',
  },
  statCard: {
    background: 'var(--agx-surface)',
    borderRadius: '12px',
    padding: '14px 16px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '1px solid #eef0f4',
    textAlign: 'center',
  },
  statVal: { fontSize: '24px', fontWeight: 700, lineHeight: '1.2' },
  statLabel: {
    fontSize: '11px',
    color: 'var(--agx-faint)',
    fontWeight: 500,
    marginTop: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  tableCard: {
    background: 'var(--agx-surface)',
    borderRadius: '14px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '1px solid #eef0f4',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '650px' },
  th: {
    textAlign: 'left',
    padding: '11px 16px',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--agx-faint)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    borderBottom: '1px solid #eef0f4',
    background: 'var(--agx-bg2)',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 16px', fontSize: '13px', color: 'var(--agx-muted)' },
  btnEdit: {
    padding: '6px 14px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--agx-blue-bg)',
    color: 'var(--agx-pri2)',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  btnDel: {
    padding: '6px 14px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--agx-red-bg)',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  btnLink: {
    padding: '5px 10px',
    borderRadius: '6px',
    border: '1px solid #e2e4e9',
    background: 'var(--agx-surface)',
    color: 'var(--agx-muted)',
    fontSize: '11px',
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  mobileCard: {
    background: 'var(--agx-surface)',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '1px solid #eef0f4',
    marginBottom: '10px',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    background: 'var(--agx-surface)',
    borderRadius: '16px',
    padding: '24px',
    width: '100%',
    maxWidth: '560px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
  },
  fg: { marginBottom: '14px' },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--agx-muted)',
    letterSpacing: '0.1px',
    display: 'block',
    marginBottom: '4px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1.5px solid #e2e4e9',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    color: 'var(--agx-text)',
  },
  btnCancel: {
    padding: '10px 20px',
    borderRadius: '10px',
    border: '1.5px solid #e2e4e9',
    background: 'var(--agx-surface)',
    color: 'var(--agx-muted)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  btnSave: {
    padding: '10px 20px',
    borderRadius: '10px',
    border: 'none',
    background: '#111827',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
  },
};

export default Sedes;
